import get from 'lodash/get'
import set from 'lodash/set'
import { identify, page, track } from './tracking-execution'
import {
  answerEvent,
  buttonEvent,
  formEvent,
  interactionFormEvent,
  interactionQuestionEvent,
  interactionQuestionMultipleChoiceEvent,
  metaEvent,
} from './events'
import getQueryParameter from '../../helper/get-query-parameter'
import { getGlobals } from '../globals'
import { increaseSequentialId } from './tracking-completion'

export function trackFormSubmit({ formId, formValues, fields, inputMap }) {
  const formField = fields.find(({ additional }) => {
    return get(additional, 'form.componentId') === formId
  })

  if (!formField) {
    return
  }

  const inputFieldList = get(formField, 'additional.form.inputs')

  const mappedFormValues = Object.keys(formValues)
    .map((fieldName) => {
      const inputId = inputMap.byName[formId][fieldName]
      const inputField = inputFieldList.find(({ componentId }) => {
        return componentId === inputId
      })

      let value = formValues[fieldName];
      if (inputField.inputType === 'checkbox') {
        if (typeof value === 'string') {
          value = value === 'true';
        }
      }

      if (inputField) {
        return {
          inputField: inputField,
          value,
          conversionTrigger: inputField.conversionTrigger,
        }
      }

      return null
    })
    .filter(Boolean)

  inputFieldList.forEach((field) => {
    const existing =
      mappedFormValues.findIndex(({ inputField }) => {
        return inputField.componentId === field.componentId
      }) > -1
    if (!existing) {
      if (field.inputType === 'checkbox') {
        mappedFormValues.push({
          inputField: field,
          value: false,
          conversionTrigger: field.conversionTrigger,
        })
      }
    }
  })

  updateUser(mappedFormValues)
  trackInteractionFormEvent(formField, mappedFormValues)
  trackFormEvent(mappedFormValues)
}

export function updateUser(mappedFormValues) {
  const identifyTraits = {}

  mappedFormValues.forEach(({ inputField, value }) => {
    set(identifyTraits, inputField.fieldName, value)
  })

  identify(identifyTraits)
}

export function trackFormEvent(mappedFormValues) {
  const name = formEvent
  const optionsName = formEvent
  const trackingObj = {}

  mappedFormValues.forEach(({ inputField, value }) => {
    trackingObj[inputField.fieldName] = value
  })

  // return track(name, trackingObj, userSensitiveData)
  return track(name, trackingObj, optionsName)
}

export function trackInteractionFormEvent(formField, mappedFormValues) {
  const name = interactionFormEvent
  const optionsName = interactionFormEvent
  const trackingObj = {
    componentId: formField.componentId, // question form
    fieldId: formField._id,
    optIns: mappedFormValues.map(({ value, inputField }) => {
      return {
        componentId: inputField.componentId, // input
        value: value,
        conversionTrigger: inputField.conversionTrigger,
      }
    }),
  }

  return track(name, trackingObj, optionsName)
}

export function trackMultipleChoiceClick({ answerIds, parentComponent, fields }) {
  const questionField = fields.find(({ componentId }) => {
    return parentComponent._id === componentId
  })

  const answerFields = questionField.additional.answers.filter(
    ({ componentId }) => {
      return answerIds.indexOf(componentId) > -1
    })

  const value = answerFields
    .map(({ value }) => value)
    .sort()
    .join('; ')

  trackInteractionQuestionMultipleChoiceEvent(questionField, answerFields,
    value)
  trackAnswerEvent(questionField, { value })
  identify({
    [questionField.title]: value,
  })
}

export function trackInteractionQuestionMultipleChoiceEvent(
  field, answerFields, value) {
  const name = interactionQuestionMultipleChoiceEvent
  const optionsName = interactionQuestionMultipleChoiceEvent
  const trackingObj = {
    componentId: field.componentId, // question text / question media
    fieldId: field._id,
    answerIds: answerFields.map(({ componentId }) => componentId),
    title: field.title,
    value: value,
  }

  return track(name, trackingObj, optionsName)
}

export function trackAnswerClick({ answer, fields }) {
  const questionField = fields.find(({ componentId }) => {
    return answer.parentId === componentId
  })

  if (!questionField) {
    return
  }

  const answerField = questionField.additional.answers.find(
    ({ componentId }) => {
      return answer._id === componentId
    })

  if (!answerField) {
    return
  }

  trackInteractionQuestionEvent(questionField, answerField)
  trackAnswerEvent(questionField.title, answerField.value)
  identify({
    [questionField.title]: answerField.value,
  })
}

export function trackInteractionQuestionEvent(field, answerField) {
  const name = interactionQuestionEvent
  const optionsName = interactionQuestionEvent
  const trackingObj = {
    componentId: field.componentId, // question text / question media
    fieldId: field._id,
    answerId: answerField.componentId,
    title: field.title,
    value: answerField.value,
    visual: answerField.visual,
    visualType: answerField.visualType,
  }

  return track(name, trackingObj, optionsName)
}

// only for 3rd-parties
export function trackAnswerEvent(title, value) {
  const name = answerEvent
  const optionsName = answerEvent
  const trackingObj = {
    [title]: value,
    question: title,
    answer: value,
  }

  return track(name, trackingObj, optionsName, true)
}

/**
 * @typedef {Object} metaDataList
 * @property {string} [fieldName = title]
 * @property {string} title
 * @property {number | string | boolean}  value
 */

/**
 *
 * @param {Object} data
 * @param {metaDataList[]} data.list
 */
export function trackMeta(data) {
  const name = metaEvent
  const optionsName = metaEvent
  const trackingObj = {
    ...data,
  }

  return track(name, trackingObj, optionsName)
}

export function trackButtonClick(id) {
  const name = id || 'button_click'
  const optionsName = buttonEvent
  const trackingObj = {}

  return track(name, trackingObj, optionsName, true)
}

export function trackPage({ name, tags }, isLastPage) {
  const queryParams = getQueryParameter()
  const trackingObj = {
    ...getGlobals(),
    utmParams: queryParams.utm,
    custom: queryParams.custom,
    pageType: tags[0],
    isLastPage,
    sequentialId: increaseSequentialId(),
  }

  return page(name, trackingObj)
}

export function trackCalendly() {
  const trackingObject = {
    list: [
      {
        fieldName: 'ps_calendly',
        title: 'Calendly Booking',
        value: true,
      },
    ],
  }

  return trackMeta(trackingObject)
}

export function trackCookies(integrations) {
  const trackingObject = {
    list: [
      {
        fieldName: 'ps_cookies',
        title: 'Cookies accepted',
        value: integrations
          .sort()
          .join('; '),
      },
    ],
  }

  return trackMeta(trackingObject)
}
